//? docs: https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';

import { warn } from '../logger';

interface initAmplitudeParams {
  sdkKey: string;
  serverZone?: amplitude.Types.ServerZoneType;
  sessionReplaySampleRate?: number;
  autocapture?: boolean;
  pageViews?: boolean;
  sessions?: boolean;
}

// Guards against re-initializing Amplitude on every client-side navigation, which
// tore down and recreated the session so session events never fired and replays had
// nothing to attach to.
let isAmplitudeInitialized = false;

//? https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#initialize-the-sdk
export const initAmplitude = (params: initAmplitudeParams) => {
  const {
    sdkKey,
    serverZone,
    sessionReplaySampleRate = 1.0,
    autocapture = false,
    pageViews = false,
    sessions = true,
  } = params;

  if (!sdkKey) {
    warn('Attempted to initialize amplitude without a key');
    return;
  }

  if (isAmplitudeInitialized) {
    return;
  }

  //? https://amplitude.com/docs/session-replay/session-replay-plugin
  const sessionReplayTracking = sessionReplayPlugin({
    sampleRate: sessionReplaySampleRate,
    // Force session tracking so a session-scoped event fires and replays can attach,
    // independent of the analytics SDK's own default-tracking behavior.
    forceSessionTracking: true,
    privacyConfig: {
      defaultMaskLevel: 'medium', // Mask sensitive fields, allow text for context
    },
  });
  amplitude.add(sessionReplayTracking);

  amplitude.init(sdkKey, {
    minIdLength: 1,
    // The session-replay plugin has no serverZone of its own; it inherits this one.
    serverZone,
    // Explicit 30-minute inactivity window so a Start Session event is emitted with a
    // stable session boundary (the comment below relied on this default being set).
    sessionTimeout: 30 * 60 * 1000,
    //? https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#autocapture
    autocapture,
    //? https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#tracking-default-events
    defaultTracking: {
      sessions, // Track session start/end with 30min inactivity timeout (caller-overridable)
      attribution: false, // UTM params, referrer - disable for now
      pageViews, // Caller-controlled; enabled for the customer + highlighter apps
      formInteractions: false,
      fileDownloads: false,
    },
  });

  // Mark initialized only after a successful init so a thrown plugin/init call doesn't
  // poison the guard and permanently no-op every later attempt until a hard reload.
  isAmplitudeInitialized = true;
};

interface SetAmplitudeUserParams {
  userId: string;
  emailAddress: string;
  orgName: string;
  orgId: string;
  userRole?: string;
  firstName?: string;
  lastName?: string;
  communitySlug?: string;
}

// ? https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#custom-user-identifier
export const setAmplitudeUser = (params: SetAmplitudeUserParams) => {
  const { userId, emailAddress, orgName, orgId, userRole, firstName, lastName, communitySlug } =
    params;

  if (!userId) {
    warn('Attempted to set amplitude user without an id');
    return;
  }

  //? https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#user-properties
  const identifyEvent = new amplitude.Identify();
  identifyEvent.set('email', emailAddress);
  identifyEvent.set('orgId', orgId);
  identifyEvent.set('orgName', orgName);

  if (userRole) {
    identifyEvent.set('role', userRole);
  }

  if (firstName) {
    identifyEvent.set('firstName', firstName);
  }

  if (lastName) {
    identifyEvent.set('lastName', lastName);
  }

  if (communitySlug) {
    identifyEvent.set('communitySlug', communitySlug);
  }

  //? https://amplitude.com/docs/get-started/identify-users#best-practices-for-setting-user-ids
  //? dont use email as id, since it can technically change
  amplitude.setUserId(userId);
  amplitude.identify(identifyEvent);

  // Set organization group for B2B analytics
  if (orgId) {
    amplitude.setGroup('organization', orgId);
  }
};

/**
 * Clears the current Amplitude identity (user id, device id, and session).
 *
 * Call on logout and when an impersonation session ends so a subsequent login on the
 * same browser starts a fresh identity and events don't bleed across users.
 *
 * @see https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#reset-when-user-logs-out
 */
export const resetAmplitudeUser = () => {
  amplitude.reset();
};

interface SetOrganizationPropertiesParams {
  orgId: string;
  orgName: string;
  teamCount?: number;
}

/**
 * Set organization-level properties using Amplitude's Group Identify API
 * This enables B2B analytics at the organization level, separate from user properties
 *
 * @see https://help.amplitude.com/hc/en-us/articles/115001765532-Accounts-in-Amplitude
 */
export const setOrganizationProperties = (params: SetOrganizationPropertiesParams) => {
  const { orgId, orgName, teamCount } = params;

  if (!orgId) {
    warn('Attempted to set organization properties without an orgId');
    return;
  }

  const groupIdentify = new amplitude.Identify();
  groupIdentify.set('name', orgName);

  if (teamCount !== undefined) {
    groupIdentify.set('team_count', teamCount);
  }

  amplitude.groupIdentify('organization', orgId, groupIdentify);
};

export { amplitude };

export * from './impersonation-context';
export * from './event-context';
export * from './insights';
export * from './page-view';
export * from './persona-chat';
export * from './setup';
export * from './auth';
export * from './types';
export * from './multi-project-dashboards';
export * from './analytics';
